import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { settingsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Settings,
  Save,
  Wifi,
  LogOut,
  Map,
  History,
  List,
  Users,
  Ruler,
  Info,
  MapPin,
} from "lucide-react";

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [settings, setSettings] = useState({ max_distance_meters: 300 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const response = await settingsApi.get();
        setSettings(response.data);
      } catch (error) {
        toast.error("Erro ao carregar configurações");
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update({ max_distance_meters: settings.max_distance_meters });
      toast.success("Configurações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  // Sidebar
  const Sidebar = () => (
    <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
            <Wifi className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">TECPLUS</h1>
            <p className="text-xs text-slate-400">NOC Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? "bg-[#FF4500] text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold">
            {user?.name?.charAt(0) || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.role?.toUpperCase()}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="settings-page">
      <Sidebar />

      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-2xl font-bold text-white">Configurações</h2>
            <p className="text-slate-400">Ajuste os parâmetros do sistema de viabilidade</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <>
              {/* Distance Settings */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Ruler className="w-5 h-5 text-[#FF4500]" />
                    Distância Máxima de Viabilidade
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Define a distância máxima em metros entre o cliente e a CTO para considerar
                    viável a instalação
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Distância máxima (metros)</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        value={settings.max_distance_meters}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            max_distance_meters: parseFloat(e.target.value) || 0,
                          })
                        }
                        min={0}
                        max={10000}
                        className="w-40 bg-slate-800 border-slate-700 text-white font-mono text-lg"
                        data-testid="max-distance-input"
                      />
                      <span className="text-slate-400">metros</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-lg">
                    <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-slate-400">
                      <p>
                        Endereços com distância menor ou igual a{" "}
                        <span className="font-mono font-bold text-white">
                          {settings.max_distance_meters}m
                        </span>{" "}
                        da CTO mais próxima serão considerados <span className="text-green-400 font-semibold">VIÁVEIS</span>.
                      </p>
                      <p className="mt-2">
                        Endereços acima dessa distância serão <span className="text-red-400 font-semibold">NÃO VIÁVEIS</span> e
                        podem ser adicionados à fila de espera.
                      </p>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
                      data-testid="save-settings-button"
                    >
                      {saving ? (
                        <div className="flex items-center gap-2">
                          <div className="spinner" />
                          <span>Salvando...</span>
                        </div>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvar Configurações
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* System Info */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Informações do Sistema</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Versão</p>
                      <p className="text-white font-mono">1.0.0</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Sistema</p>
                      <p className="text-white">Tecplus Fiber OS</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Algoritmo</p>
                      <p className="text-white font-mono">Haversine</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Formato Suportado</p>
                      <p className="text-white font-mono">KMZ / KML</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
